import React from 'react';
import { Skeleton, SkeletonPanel, SkeletonLines } from './ui/skeleton';

/**
 * PageSkeleton (CC8): the route-level loading frame shown by Suspense while a
 * page chunk loads. It mirrors the shape most pages settle into (eyebrow,
 * display title, subtitle, a hero band, then a two-column panel grid) on the
 * Card chrome, with the teal shimmer, so the first paint and the final paint
 * share the same edges. `variant="wide"` drops the side column for table and
 * admin pages.
 */
const PageSkeleton: React.FC<{ variant?: 'default' | 'wide' }> = ({ variant = 'default' }) => {
  return (
    <div
      className="lnr-page-skeleton"
      aria-busy="true"
      aria-label="Loading"
      style={{ display: 'flex', flexDirection: 'column', gap: 18 }}
    >
      {/* PageHeader: eyebrow, title, description, action */}
      <div
        className="lnr-skeleton-row"
        style={{ justifyContent: 'space-between', alignItems: 'flex-end', gap: 24, minHeight: 92 }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1, minWidth: 0 }}>
          <Skeleton variant="pill" width={140} height={14} />
          <Skeleton variant="title" width="min(360px, 70%)" height={34} />
          <Skeleton variant="text" width="min(460px, 90%)" />
        </div>
        <Skeleton variant="pill" width={132} height={40} />
      </div>

      {/* Hero band */}
      <SkeletonPanel
        style={{
          minHeight: 220,
          padding: 28,
          flexDirection: 'row',
          gap: 32,
          alignItems: 'stretch',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, flex: 1.25, minWidth: 0 }}>
          <Skeleton variant="pill" width={120} height={14} />
          <Skeleton variant="title" width="80%" height={36} />
          <SkeletonLines lines={2} />
          <Skeleton variant="pill" width={200} height={44} style={{ marginTop: 'auto' }} />
        </div>
        <Skeleton
          variant="tile"
          style={{ flex: 1, minWidth: 0, minHeight: 160 }}
          className="lnr-page-skeleton__side"
        />
      </SkeletonPanel>

      {/* Panel grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns:
            variant === 'wide'
              ? 'minmax(0, 1fr)'
              : 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
          gap: 18,
        }}
      >
        <SkeletonPanel
          style={{ minHeight: 320, gridColumn: variant === 'wide' ? 'auto' : 'span 1' }}
        >
          <Skeleton variant="pill" width={110} height={14} />
          <Skeleton variant="title" width="55%" height={24} />
          <Skeleton variant="tile" height={200} />
        </SkeletonPanel>
        {variant !== 'wide' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <SkeletonPanel style={{ minHeight: 150 }}>
              <Skeleton variant="pill" width={90} height={14} />
              <SkeletonLines lines={3} />
            </SkeletonPanel>
            <SkeletonPanel style={{ flex: 1, minHeight: 150 }}>
              <Skeleton variant="pill" width={90} height={14} />
              <div className="lnr-skeleton-row">
                <Skeleton width={44} height={44} style={{ borderRadius: 12 }} />
                <SkeletonLines lines={2} style={{ flex: 1 }} />
              </div>
            </SkeletonPanel>
          </div>
        )}
      </div>
    </div>
  );
};

export default PageSkeleton;
